import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../App";

const GoogleSignInButton = ({ className = "" }) => {
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = encodeURIComponent("openid email profile");
    const responseType = "id_token";
    const nonce = Math.random().toString(36).substring(2);

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?prompt=consent&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=${responseType}&nonce=${nonce}`;
  };

  return (
    <button
      onClick={handleGoogleLogin}
      className={`inline-flex items-center justify-center gap-3 px-6 py-2.5 bg-white border border-gray-300 rounded-full shadow-sm hover:shadow-md hover:bg-gray-50 transition-all text-sm font-semibold text-gray-700 ${className}`}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Sign in with Google
    </button>
  );
};

const AuthShell = ({ children }) => (
  <div className="min-h-screen bg-background font-body flex flex-col">
    <header className="px-6 md:px-12 py-6">
      <Link to="/" className="inline-flex items-center gap-2">
        <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
        <span className="text-xl font-extrabold tracking-tighter font-headline text-foreground">
          PostWriter
        </span>
      </Link>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 pb-16">
      <div className="w-full max-w-sm">{children}</div>
    </main>
  </div>
);

const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [checkEmail, setCheckEmail] = useState(null); // { email } once signup succeeds
  const [isResending, setIsResending] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/register", formData);
      setCheckEmail({ email: data.email });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code) {
        setError(detail);
      } else {
        setError({ code: "unknown", message: "Something went wrong. Please try again." });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!checkEmail) return;
    setIsResending(true);
    try {
      await api.post("/auth/resend-verification", { email: checkEmail.email });
    } finally {
      setIsResending(false);
    }
  };

  if (checkEmail) {
    return (
      <AuthShell>
        <Helmet>
          <title>Check your email — PostWriter</title>
        </Helmet>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary mb-6">
            <Mail className="w-5 h-5 text-foreground" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-3">
            Check your email
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8">
            We sent a verification link to <span className="text-foreground font-medium">{checkEmail.email}</span>.
            Click it to activate your account, then log in.
          </p>
          <Button
            variant="outline"
            className="w-full mb-4"
            onClick={handleResend}
            disabled={isResending}
          >
            {isResending ? "Sending…" : "Resend verification email"}
          </Button>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Already verified? Log in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Helmet>
        <title>Sign up — PostWriter</title>
      </Helmet>
      <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-1">
        Create your account
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Start writing LinkedIn posts that sound like you.
      </p>

      <GoogleSignInButton className="w-full mb-6" />

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-3 text-muted-foreground font-headline tracking-wider">or sign up with email</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            autoComplete="name"
            value={formData.name}
            onChange={handleChange("name")}
            required
          />
          {error?.code === "invalid_name" && (
            <p className="text-xs text-destructive">{error.message}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange("email")}
            required
          />
          {error?.code === "invalid_email" && (
            <p className="text-xs text-destructive">{error.message}</p>
          )}
          {error?.code === "provider_conflict" && (
            <p className="text-xs text-destructive">{error.message}</p>
          )}
          {error?.code === "email_exists" && (
            <p className="text-xs text-destructive">
              {error.message} <Link to="/login" className="underline">Log in</Link>
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange("password")}
            required
          />
          {error?.code === "weak_password" && (
            <p className="text-xs text-destructive">{error.message}</p>
          )}
        </div>
        {error?.code === "unknown" && (
          <p className="text-xs text-destructive">{error.message}</p>
        )}
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Creating account…" : "Create account"}
          {!isSubmitting && <ArrowRight className="w-4 h-4" />}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/login" className="text-foreground font-medium hover:text-primary transition-colors">
          Log in
        </Link>
      </p>
    </AuthShell>
  );
};

export default SignupPage;
